import { useRef, useState } from "react";
import { Group, NumberField, RefsPicker, Select, TextArea, TextField, UpIcon, XIcon, move } from "../fields";
import { uniqueId } from "../model";
import type { B, EditorProps } from "./types";

type Pin = B<"screenshot">["pins"][number];

const round = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000;

/** The image with its numbered pins. Drag a pin to place it; positions are stored as 0–1 fractions. */
function PinCanvas({
  src,
  pins,
  active,
  onActive,
  onMove,
}: {
  src: string | null;
  pins: Pin[];
  active: number;
  onActive: (i: number) => void;
  onMove: (i: number, x: number, y: number) => void;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(758 / 470);
  const drag = useRef<number | null>(null);

  const place = (e: React.PointerEvent) => {
    const i = drag.current;
    const r = stage.current?.getBoundingClientRect();
    if (i === null || !r) return;
    onMove(i, round((e.clientX - r.left) / r.width), round((e.clientY - r.top) / r.height));
  };

  return (
    <div
      className="pin-stage"
      ref={stage}
      style={{ aspectRatio: String(ratio) }}
      onPointerMove={place}
      onPointerUp={() => (drag.current = null)}
    >
      {src ? (
        <img
          src={src}
          alt=""
          onLoad={(e) => setRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || ratio)}
        />
      ) : (
        <div className="pin-stage-empty">
          No image yet. Upload one or pick an uploaded file above; you can place pins now.
        </div>
      )}
      {pins.map((p, i) => (
        <button
          key={p.id}
          type="button"
          className="pin-dot"
          data-active={i === active}
          style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
          aria-label={`Pin ${i + 1}. Drag to move; arrow keys nudge.`}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            drag.current = i;
            onActive(i);
          }}
          onPointerMove={place}
          onPointerUp={() => (drag.current = null)}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 0.05 : 0.01;
            const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[
              e.key
            ];
            if (!d) return;
            e.preventDefault();
            onMove(i, round(p.x + d[0]!), round(p.y + d[1]!));
          }}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
}

export function ScreenshotEditor({ value: b, onChange, deck, assets, upload }: EditorProps<B<"screenshot">>) {
  const set = (patch: Partial<B<"screenshot">>) => onChange({ ...b, ...patch });
  const [active, setActive] = useState(0);
  const [uploading, setUploading] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const setPin = (i: number, patch: Partial<Pin>) =>
    set({ pins: b.pins.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => set({ title })} />
      <div className="grid-2">
        <Select
          label="Image"
          value={b.assetId ?? ""}
          options={[
            { value: "", label: "No image yet" },
            ...assets
              .filter((a) => a.contentType.startsWith("image/"))
              .map((a) => ({ value: a.id, label: a.filename })),
          ]}
          onChange={(id) => {
            const a = assets.find((x) => x.id === id);
            set({ assetId: id || null, ...(a && { filename: a.filename }) });
          }}
        />
        <div className="field">
          <span className="field-label">Upload</span>
          <button type="button" className="s-btn" disabled={uploading} onClick={() => file.current?.click()}>
            {uploading ? "Uploading…" : "Upload image"}
          </button>
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setUploading(true);
              const a = await upload(f);
              setUploading(false);
              if (a) set({ assetId: a.id, filename: a.filename });
            }}
          />
        </div>
      </div>
      <TextField
        label="File name"
        hint="Notes that say IMAGE: this-name.png attach the upload with this name."
        mono
        max={120}
        value={b.filename}
        onChange={(filename) => set({ filename })}
      />

      <PinCanvas
        src={b.assetId ? `/api/assets/${encodeURIComponent(b.assetId)}` : null}
        pins={b.pins}
        active={active}
        onActive={setActive}
        onMove={(i, x, y) => setPin(i, { x, y })}
      />

      {b.pins.map((p, i) => (
        <Group
          key={p.id}
          title={`Pin ${i + 1}`}
          id={`${Math.round(p.x * 100)}%, ${Math.round(p.y * 100)}%`}
          actions={
            <>
              <button
                type="button"
                className="icon-btn"
                aria-label="Move up"
                disabled={i === 0}
                onClick={() => set({ pins: move(b.pins, i, i - 1) })}
              >
                <UpIcon />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Move down"
                disabled={i === b.pins.length - 1}
                onClick={() => set({ pins: move(b.pins, i, i + 1) })}
              >
                <UpIcon down />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Remove pin"
                onClick={() => set({ pins: b.pins.filter((_, j) => j !== i) })}
              >
                <XIcon />
              </button>
            </>
          }
        >
          <TextArea
            label="Question"
            max={140}
            rows={2}
            value={p.question}
            onChange={(question) => setPin(i, { question })}
          />
          <NumberField
            label="Zoom (1–4×)"
            min={1}
            max={4}
            step={0.1}
            value={p.zoom}
            onChange={(zoom) => setPin(i, { zoom: zoom ?? 1.9 })}
          />
        </Group>
      ))}
      {b.pins.length < 9 && (
        <button
          type="button"
          className="s-btn s-btn-dashed"
          style={{ alignSelf: "flex-start" }}
          onClick={() => {
            set({
              pins: [
                ...b.pins,
                {
                  id: uniqueId(
                    "pin",
                    b.pins.map((p) => p.id),
                  ),
                  x: 0.5,
                  y: 0.5,
                  question: "",
                  zoom: 1.9,
                },
              ],
            });
            setActive(b.pins.length);
          }}
        >
          + Pin
        </button>
      )}
      <TextArea
        label="Caption"
        max={200}
        value={b.caption}
        onChange={(caption) => set({ caption: caption || undefined })}
      />
      <RefsPicker references={deck.references} value={b.refs} onChange={(refs) => set({ refs })} />
      <TextArea
        label="Speaker notes"
        max={2000}
        value={b.notes}
        onChange={(notes) => set({ notes: notes || undefined })}
      />
    </>
  );
}
